import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { ConsensusActivityRender } from "components/pages/Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { buildPreviewChallenge } from "utils/challenge";
import "./exampleQuestionsCarousel.css";

/**
 * Renders a navigable list of example questions using the same rich question
 * display as the Differentiation standard-assign flow (ConsensusActivityRender):
 * prompt + image + multiple-choice answer pills, with prev/next controls and a
 * "Problem X of Y" counter. Expects consensus-shaped question objects.
 */
// `background` (optional) overrides the nav + question area fill so the carousel can
// blend into a host page whose background isn't the default #FAF7F2.
export const ExampleQuestionsCarousel = ({ questions = [], loading = false, total, onLoadMore, background }) => {
  const [index, setIndex] = useState(0);
  const bgStyle = background ? { backgroundColor: background } : undefined;

  // Full count when paginating (e.g. 141), else the loaded slice.
  const count = typeof total === "number" && total > questions.length ? total : questions.length;
  const firstId = questions.length > 0 ? questions[0].id : null;

  // Reset only when the underlying set truly changes (lesson switch), keyed on the
  // first question — appending the next page must NOT snap back to problem 1.
  useEffect(() => {
    setIndex(0);
  }, [firstId]);

  // Same 10-per-10 pagination as the standard flow: at the end of the loaded set,
  // fetch the next page (offset = loaded count) and advance onto it.
  const handleNext = async () => {
    if (index < questions.length - 1) {
      setIndex(index + 1);
      return;
    }
    if (onLoadMore) {
      const more = await onLoadMore();
      if (more && more.length > 0) setIndex(index + 1);
    }
  };

  if (loading) {
    return (
      <div className="eqc-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </div>
    );
  }

  if (!questions || questions.length === 0) return null;

  const current = questions[Math.min(index, questions.length - 1)];

  return (
    <div className="eqc-container">
      <div className="eqc-navigation" style={bgStyle}>
        <button
          className="eqc-nav-button"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          aria-label="Previous question"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className="eqc-counter">
          Problem {index + 1} of {count}
        </span>
        <button
          className="eqc-nav-button"
          onClick={handleNext}
          disabled={index >= count - 1}
          aria-label="Next question"
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ transform: "rotate(180deg)" }} />
        </button>
      </div>
      <div className="eqc-question" style={bgStyle}>
        <ConsensusActivityRender
          challenge={buildPreviewChallenge(current)}
          onMCAnswer={() => {}}
          onORAnswer={() => {}}
          isMathJaxSet={false}
          disableOptions={true}
          showBorder={true}
          showPromptFormula
        />
      </div>
    </div>
  );
};
